import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, ArrowLeft, Check, Loader2, RefreshCw, Sparkles } from "lucide-react";
import {
  API,
  getSessionId,
  saveLastConsultationId,
} from "@/lib/api";
import { track } from "@/lib/analytics";
import { useLang } from "@/lib/i18n";
import Sparks from "@/components/Sparks";

/**
 * Three real phases streamed from the backend. Titles/subtitles are resolved
 * at render time via the i18n dictionary so they follow the user's chosen
 * language.
 */
const PHASE_KEYS = ["psychology", "astro_num", "synthesis"];

function parseSSE(buffer) {
  // Parse ALL complete "event: X\ndata: Y\n\n" blocks in the buffer.
  // Returns { events: [...], rest: unfinishedString }.
  const events = [];
  const chunks = buffer.split(/\n\n/);
  const rest = chunks.pop() ?? "";
  for (const chunk of chunks) {
    if (!chunk.trim()) continue;
    const lines = chunk.split("\n");
    let event = "message";
    const dataLines = [];
    for (const line of lines) {
      if (line.startsWith("event:")) {
        event = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).trim());
      }
    }
    let data = null;
    if (dataLines.length) {
      try {
        data = JSON.parse(dataLines.join("\n"));
      } catch (_e) {
        data = { raw: dataLines.join("\n") };
      }
    }
    events.push({ event, data });
  }
  return { events, rest };
}

export default function Processing() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { lang, t } = useLang();

  const PHASES = PHASE_KEYS.map((key, i) => ({
    key,
    title: t(`proc.phase${i + 1}.title`),
    subtitle: t(`proc.phase${i + 1}.subtitle`),
  }));

  const ERROR_COPY = {
    llm_budget_exceeded: {
      title: t("err.llm_budget_exceeded.title"),
      body: t("err.llm_budget_exceeded.body"),
      ctaLabel: t("common.retry"),
      tone: "warm",
    },
    llm_quota_exceeded: {
      title: t("err.llm_quota_exceeded.title"),
      body: t("err.llm_quota_exceeded.body"),
      ctaLabel: t("common.retry"),
      tone: "warm",
    },
    llm_auth_failed: {
      title: t("err.llm_auth_failed.title"),
      body: t("err.llm_auth_failed.body"),
      ctaLabel: t("common.retry"),
      tone: "warn",
    },
    llm_bad_json: {
      title: t("err.llm_bad_json.title"),
      body: t("err.llm_bad_json.body"),
      ctaLabel: t("common.retry"),
      tone: "warm",
    },
    llm_transient: {
      title: t("err.llm_transient.title"),
      body: t("err.llm_transient.body"),
      ctaLabel: t("common.retry"),
      tone: "warm",
    },
    llm_not_configured: {
      title: t("err.llm_not_configured.title"),
      body: t("err.llm_not_configured.body"),
      ctaLabel: t("common.retry"),
      tone: "warn",
    },
    network: {
      title: t("err.network.title"),
      body: t("err.network.body"),
      ctaLabel: t("common.retry"),
      tone: "warm",
    },
    llm_unknown: {
      title: t("err.llm_unknown.title"),
      body: t("err.llm_unknown.body"),
      ctaLabel: t("common.retry"),
      tone: "warm",
    },
  };

  // phase status keyed by phase key: "pending" | "active" | "complete"
  const [phaseStatus, setPhaseStatus] = useState({
    psychology: "pending",
    astro_num: "pending",
    synthesis: "pending",
  });
  const [phaseData, setPhaseData] = useState({});
  const [elapsedMs, setElapsedMs] = useState(0);
  const [retryNotice, setRetryNotice] = useState(null);
  const [errorState, setErrorState] = useState(null); // {code, message, retryable}
  const [attemptId, setAttemptId] = useState(0);

  const abortRef = useRef(null);
  const startTimeRef = useRef(null);

  // Local ticking elapsed timer while running.
  useEffect(() => {
    if (errorState) return undefined;
    if (!startTimeRef.current) return undefined;
    const t = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 250);
    return () => clearInterval(t);
  }, [errorState, attemptId]);

  const startStream = useCallback(async () => {
    if (!state?.birth || !state?.category) {
      navigate("/onboarding");
      return;
    }

    // Reset for a fresh attempt.
    setPhaseStatus({ psychology: "pending", astro_num: "pending", synthesis: "pending" });
    setPhaseData({});
    setElapsedMs(0);
    setRetryNotice(null);
    setErrorState(null);
    startTimeRef.current = Date.now();

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const sid = getSessionId();
      const res = await fetch(`${API}/consultations/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify({
          birth: state.birth,
          category: state.category,
          challenge: state.challenge || "",
          profile: state.profile || null,
          session_id: sid,
          language: lang,
          personal_context: state.personal_context || "",
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        // Non-200 (rare — the stream normally returns 200 and embeds errors as SSE frames).
        setErrorState({
          code: "network",
          message: `Server returned ${res.status}. Please try again.`,
          retryable: true,
        });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let redirectHandled = false;

       
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { events, rest } = parseSSE(buffer);
        buffer = rest;

        for (const { event, data } of events) {
          if (event === "phase") {
            const key = data?.key;
            if (!key) continue;
            setPhaseStatus((prev) => ({
              ...prev,
              [key]: data.status === "complete" ? "complete" : "active",
            }));
            if (data.status === "complete") {
              setPhaseData((prev) => ({ ...prev, [key]: data }));
            }
          } else if (event === "heartbeat") {
            if (typeof data?.elapsed_ms === "number") {
              setElapsedMs(data.elapsed_ms);
            }
          } else if (event === "retry") {
            setRetryNotice({
              attempt: data?.attempt ?? 1,
              message: data?.message || "Retrying…",
              at: Date.now(),
            });
          } else if (event === "paywall") {
            // Identity has exhausted its free reading — hand off to /paywall.
            try {
              if (data?.paywall) {
                localStorage.setItem("mantrana_paywall", JSON.stringify(data.paywall));
              }
              localStorage.setItem("mantrana_free_chat_used", "true");
            } catch (_) {
              /* ignore */
            }
            redirectHandled = true;
            navigate("/paywall");
            return;
          } else if (event === "complete") {
            const id = data?.consultation_id;
            if (id) {
              saveLastConsultationId(id);
              track("consultation_created", { consultation_id: id });
              // let the "Done" state render for a beat
              redirectHandled = true;
              setTimeout(() => navigate(`/results/${id}`), 900);
            }
          } else if (event === "error") {
            setErrorState({
              code: data?.code || "llm_unknown",
              message: data?.message || "Something went wrong.",
              retryable: data?.retryable !== false,
            });
            return;
          }
        }
      }

      if (!redirectHandled) {
        // Stream ended without a complete/error/paywall frame — treat as network drop.
        setErrorState({
          code: "network",
          message: "The connection to the clarity engine ended unexpectedly.",
          retryable: true,
        });
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
       
      console.error("stream error", err);
      setErrorState({
        code: "network",
        message: err?.message || "Network error contacting the clarity engine.",
        retryable: true,
      });
    }
     
  }, [state, navigate]);

  useEffect(() => {
    startStream();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
     
  }, [attemptId]);

  const handleRetry = () => {
    if (abortRef.current) abortRef.current.abort();
    setAttemptId((n) => n + 1);
  };

  const handleBack = () => {
    if (abortRef.current) abortRef.current.abort();
    navigate("/onboarding");
  };

  // ------- ERROR STATE -------
  if (errorState) {
    const copy = ERROR_COPY[errorState.code] || ERROR_COPY.llm_unknown;
    const toneRing =
      copy.tone === "warn"
        ? "from-rose-400/40 to-transparent"
        : "from-amber-300/40 to-transparent";

    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center px-6 relative">
        <Sparks />
        <div className="max-w-xl w-full relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="glass rounded-3xl p-8 md:p-10 text-center"
            data-testid="processing-error-card"
          >
            <div className="flex flex-col items-center">
              <div
                className={`w-16 h-16 rounded-full bg-gradient-to-b ${toneRing} flex items-center justify-center mb-6`}
              >
                <AlertCircle
                  className="w-7 h-7"
                  style={{ color: copy.tone === "warn" ? "#fda4af" : "#D4AF37" }}
                  strokeWidth={1.5}
                />
              </div>
              <div className="text-[10px] tracking-[0.35em] uppercase gold-text mb-3">
                {errorState.code === "llm_budget_exceeded"
                  ? t("proc.err.pauseTitle")
                  : t("proc.err.smallTitle")}
              </div>
              <h1
                className="font-serif text-2xl md:text-3xl font-light text-white leading-tight"
                data-testid="processing-error-title"
              >
                {copy.title}
              </h1>
              <p
                className="mt-4 text-sm md:text-base font-light text-slate-300 leading-relaxed max-w-md"
                data-testid="processing-error-body"
              >
                {copy.body}
              </p>

              {errorState.message && errorState.message !== copy.body && (
                <div className="mt-4 text-[11px] tracking-wide text-slate-500 max-w-md">
                  {errorState.message}
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                <button
                  onClick={handleRetry}
                  className="w-full sm:w-auto px-8 py-3 rounded-full font-medium text-sm tracking-wide bg-gradient-to-r from-amber-300 to-yellow-500 text-slate-900 hover:brightness-105 transition inline-flex items-center justify-center gap-2"
                  data-testid="processing-retry-btn"
                >
                  <RefreshCw className="w-4 h-4" strokeWidth={2} />
                  {copy.ctaLabel}
                </button>
                <button
                  onClick={handleBack}
                  className="w-full sm:w-auto px-6 py-3 rounded-full font-light text-sm tracking-wide text-slate-300 hover:text-white transition inline-flex items-center justify-center gap-2"
                  data-testid="processing-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                  {t("proc.err.back")}
                </button>
              </div>

              <div className="mt-6 text-[10px] tracking-[0.25em] uppercase text-slate-600">
                {t("proc.footer.saved")}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // ------- ACTIVE STATE -------
  const seconds = Math.floor(elapsedMs / 1000);
  const psychology = phaseData.psychology;
  const astroNum = phaseData.astro_num;

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center px-6 relative">
      <Sparks />
      {/* Ambient constellation */}
      <svg
        aria-hidden="true"
        viewBox="0 0 800 600"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full pointer-events-none opacity-50"
      >
        <defs>
          <linearGradient id="proc-conn" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="rgba(212,175,55,0.7)" />
            <stop offset="100%" stopColor="rgba(212,175,55,0.05)" />
          </linearGradient>
        </defs>
        {[
          [80, 90], [180, 60], [260, 140], [360, 80], [460, 160], [560, 90],
          [640, 180], [120, 240], [240, 320], [340, 260], [480, 360], [620, 320],
          [180, 460], [320, 500], [460, 460], [600, 520], [720, 420], [60, 380],
        ].map(([x, y], i) => (
          <motion.circle
            key={`pn-${i}`}
            cx={x}
            cy={y}
            r="1.5"
            fill="rgba(212,175,55,0.9)"
            initial={{ opacity: 0.2 }}
            animate={{ opacity: [0.25, 0.9, 0.25] }}
            transition={{ duration: 3 + (i % 4), repeat: Infinity, delay: i * 0.18 }}
          />
        ))}
        {[
          [80, 90, 180, 60], [180, 60, 260, 140], [260, 140, 360, 80],
          [360, 80, 460, 160], [460, 160, 560, 90], [560, 90, 640, 180],
          [120, 240, 240, 320], [240, 320, 340, 260], [340, 260, 480, 360],
          [480, 360, 620, 320], [180, 460, 320, 500], [320, 500, 460, 460],
          [460, 460, 600, 520], [600, 520, 720, 420], [60, 380, 120, 240],
          [240, 320, 460, 160],
        ].map(([x1, y1, x2, y2], i) => (
          <motion.line
            key={`pe-${i}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="url(#proc-conn)"
            strokeWidth="0.8"
            initial={{ opacity: 0.1 }}
            animate={{ opacity: [0.15, 0.5, 0.15] }}
            transition={{ duration: 5 + (i % 3), repeat: Infinity, delay: i * 0.22 }}
          />
        ))}
      </svg>

      <div className="max-w-2xl w-full relative z-10">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-8">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="relative w-20 h-20 rounded-full flex items-center justify-center pulse-ring"
            style={{
              background: "radial-gradient(circle, rgba(212,175,55,0.3), transparent 70%)",
            }}
          >
            <Sparkles className="w-8 h-8 gold-text" strokeWidth={1.5} />
          </motion.div>
          <div className="mt-6 text-xs tracking-[0.3em] uppercase gold-text">
            {t("proc.eyebrow")}
          </div>
          <h1 className="mt-3 font-serif text-3xl md:text-4xl font-light text-white leading-tight">
            {t("proc.title.a")} <span className="italic gold-text">{t("proc.title.b")}</span>...
          </h1>

          {state?.birth?.full_name && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-4 text-sm md:text-base font-light text-slate-300"
              data-testid="processing-personalization-line"
            >
              {t("proc.personalize")}{" "}
              <span className="gold-text font-serif italic">
                {state.birth.full_name.trim().split(/\s+/)[0]}
              </span>
            </motion.div>
          )}

          {state?.birth && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[10px] tracking-[0.2em] uppercase text-slate-400"
              data-testid="processing-context-strip"
            >
              <span>{state.birth.full_name}</span>
              <span className="text-slate-600">·</span>
              <span>{state.birth.date_of_birth}</span>
              <span className="text-slate-600">·</span>
              <span>{state.birth.time_of_birth}</span>
              <span className="text-slate-600">·</span>
              <span>{state.birth.place_of_birth}</span>
              {state.category && (
                <>
                  <span className="text-slate-600">·</span>
                  <span className="gold-text">{t(`cat.${state.category}`)}</span>
                </>
              )}
            </motion.div>
          )}
        </div>

        {/* Retry banner (transient) */}
        <AnimatePresence>
          {retryNotice && (
            <motion.div
              key={retryNotice.at}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 text-[12px] tracking-wide text-amber-100/90 flex items-center gap-2"
              data-testid="processing-retry-notice"
            >
              <RefreshCw className="w-3.5 h-3.5 animate-spin" strokeWidth={2} />
              {retryNotice.message}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Phase list */}
        <div className="space-y-2.5">
          {PHASES.map((phase, i) => {
            const status = phaseStatus[phase.key];
            const isDone = status === "complete";
            const isActive = status === "active";
            const data = phaseData[phase.key];

            return (
              <motion.div
                key={phase.key}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.08 }}
                className="glass rounded-2xl px-5 py-4 flex items-start gap-4"
                style={
                  isActive
                    ? {
                        borderColor: "rgba(212,175,55,0.4)",
                        boxShadow: "0 0 24px rgba(212,175,55,0.18)",
                      }
                    : undefined
                }
                data-testid={`processing-phase-${phase.key}`}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{
                    background: isDone ? "#D4AF37" : "rgba(255,255,255,0.06)",
                  }}
                >
                  {isDone ? (
                    <Check className="w-4 h-4" style={{ color: "#0B1020" }} strokeWidth={2.5} />
                  ) : isActive ? (
                    <Loader2 className="w-4 h-4 text-slate-300 animate-spin" />
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div
                    className={`font-light text-[15px] leading-snug ${
                      isDone || isActive ? "text-white" : "text-slate-500"
                    }`}
                  >
                    {phase.title}
                  </div>
                  <div
                    className={`mt-0.5 text-[11px] leading-snug ${
                      isDone || isActive ? "text-slate-400" : "text-slate-600"
                    }`}
                  >
                    {phase.subtitle}
                  </div>

                  {/* Live-populated evidence when a phase completes */}
                  <AnimatePresence>
                    {isDone && data?.headline && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-2 text-[11px] tracking-wide text-emerald-200/80"
                      >
                        ✓ {data.headline}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Phase-specific pills */}
                  {isDone && phase.key === "psychology" && psychology?.dimensions && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {Object.entries(psychology.dimensions)
                        .slice(0, 4)
                        .map(([k, v]) => (
                          <span
                            key={k}
                            className="text-[10px] tracking-wide uppercase px-2 py-0.5 rounded-full border border-white/10 text-slate-300"
                          >
                            {k.replace(/_/g, " ")}: {typeof v === "number" ? v.toFixed(1) : String(v).slice(0, 12)}
                          </span>
                        ))}
                    </div>
                  )}
                  {isDone && phase.key === "astro_num" && astroNum?.numerology && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className="text-[10px] tracking-wide uppercase px-2 py-0.5 rounded-full border border-white/10 text-slate-300">
                        {t("proc.numerology.lifePath")} {astroNum.numerology.life_path}
                      </span>
                      <span className="text-[10px] tracking-wide uppercase px-2 py-0.5 rounded-full border border-white/10 text-slate-300">
                        {t("proc.numerology.expression")} {astroNum.numerology.expression}
                      </span>
                      <span className="text-[10px] tracking-wide uppercase px-2 py-0.5 rounded-full border border-white/10 text-slate-300">
                        {t("proc.numerology.personalYear")} {astroNum.numerology.personal_year}
                      </span>
                    </div>
                  )}
                </div>

                <div
                  className={`text-[10px] tracking-[0.25em] uppercase mt-1 whitespace-nowrap ${
                    isDone
                      ? "text-emerald-400/80"
                      : isActive
                      ? "gold-text"
                      : "text-slate-600"
                  }`}
                >
                  {isDone ? t("proc.status.done") : isActive ? t("proc.status.now") : `0${i + 1}`}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Live footer — elapsed + reassurance */}
        <div className="mt-6 flex items-center justify-between text-[10px] tracking-[0.25em] uppercase text-slate-500">
          <span data-testid="processing-elapsed">
            {seconds > 0
              ? `${seconds}s · ${t("proc.elapsed")}`
              : t("proc.elapsed")}
          </span>
          <span>
            {t("proc.phase.of", {
              i: Math.min(
                1 + PHASES.filter((p) => phaseStatus[p.key] !== "pending").length - 1,
                3,
              ),
            })}
          </span>
        </div>
      </div>
    </div>
  );
}
